// La "vuelta atrás" de Interfaz V2: qué había abierto y elegido cuando se disparó la
// última generación.
//
// Al generar, `InterfazV2` guarda acá el estado completo de la UI. Al montar, lo lee y
// reabre el modo en SU paso (`pasoDeConfiguracion`), para que el "Atrás" desde la
// pantalla de imágenes generadas devuelva exactamente lo que se generó.
//
// La clave vive en su propio módulo —y no como string suelto en las dos pantallas que la
// tocan— porque quien la ESCRIBE (`InterfazV2`) y quien la BORRA (los CTA de la pantalla
// de resultados) son archivos distintos, y un literal repetido en dos lados se
// desincroniza en el primer renombre. Ver
// ADR-2026-09-04-listo-no-reabre-la-configuracion-del-modo.
//
// ── Por qué el contrato está acá y no suelto en el componente ────────────────────
//
// Hasta ADR-2026-09-10-el-restore-guarda-la-configuracion-entera el blob era un objeto
// literal sin tipo con 12 campos, y la lectura, una tira de `if (blob.x) setX(...)`. Dos
// listas escritas a mano que había que acordarse de tocar juntas — y no se tocaban: de
// los 20 campos que configura un modo, el blob guardaba 12. Volver de generar un Anuncio
// perdía título, descripción, llamada a acción, colores y tipografías; un Video, el
// guion, la voz, la duración y el gancho; todos, la Calidad y la Cantidad.
//
// Ahora son dos piezas que el compilador y un test mantienen pegadas:
//   • `RestoreV2` — el contrato. El objeto que arma `InterfazV2` está TIPADO con esto, así
//     que un campo nuevo sin escribir no compila.
//   • `aplicarRestoreV2` — la lectura, pura y con un test de ida y vuelta: lo que se
//     guarda se recupera idéntico. Un campo escrito y no leído lo hace fallar.

import type { AnuncioFieldState } from './panels/AnuncioPanel';
import type { VoiceGuionValue } from './components/VoiceGuionSection';
import type { FondoSelection } from './FondoPicker';
import type { ModelSelection } from './ModelPicker';
import type { VistaCatalogo } from './vistasCatalogo';
import type { ResolucionImagen } from '../../lib/resolucionImagen';

export const CLAVE_RESTORE_V2 = 'atlas-v2-restore';

/**
 * TODO lo que define una generación de Interfaz V2 — la misma superficie que el modo le
 * muestra al usuario (y que el agente lee y escribe por el puente, `camposDeModo.ts`).
 *
 * Si un modo gana un campo configurable, va acá. Es lo que separa "volver a lo que
 * generaste" de "volver al modo y rehacerlo".
 */
export interface RestoreV2 {
  /** El modo/plantilla abierto. Se resuelve contra el catálogo al montar. */
  presetId: string | null;
  /** Productos elegidos, por id: los objetos se resuelven contra `userProducts`. */
  selectedProductIds: string[];
  /** Productos por slot (outfits de varios modelos), por id. */
  multiSlotProductIds: Record<string, string[]>;
  /** "Sin producto" del influencer (ADR-673): no es "ninguno", es una elección. */
  sinProducto: boolean;
  /** El anuncio salió de una creación y no de un producto. */
  imagenBase: { id: string; url: string } | null;
  modelSelection: ModelSelection | null;
  modelSelectionsSerie: ModelSelection[];
  modelSelectionsExtra: (ModelSelection | null)[];
  multiModelEnabled: boolean;
  poseIds: string[];
  multiPoseEnabled: boolean;
  fondoSelection: FondoSelection | null;
  formatoVal: string | null;
  /** Calidad (1K/2K/4K). No viajaba: se volvía siempre con el default. */
  resolucionVal: ResolucionImagen;
  otraConfig: string;
  /** Cantidad de imágenes. No viajaba: se volvía siempre con 1. */
  autoImageCount: number;
  /** Vistas de "Fotos de producto". */
  vistaIds: string[];
  /** Vistas del Catálogo (objetos, no ids: el picker mezcla catálogos distintos). */
  catalogoVistas: VistaCatalogo[];
  /** Qué productos se sacaron de cada vista, en Fotos de producto. */
  vistaProductosExcluidos: Record<string, string[]>;
  /** Plantillas elegidas con "Seleccionar varias". */
  multiPresetIds: string[];
  anuncio: {
    titulo: AnuncioFieldState;
    descripcion: AnuncioFieldState;
    cta: AnuncioFieldState;
    colores: string[];
  };
  video: {
    duracion: number;
    multiEscena: boolean;
    ganchoId: string | null;
    voz: VoiceGuionValue;
  };
}

/** Los setters que `aplicarRestoreV2` necesita para volver a poner la configuración. */
export interface SettersDeRestoreV2 {
  setSinProducto: (v: boolean) => void;
  setImagenBase: (v: RestoreV2['imagenBase']) => void;
  setModelSelection: (v: ModelSelection | null) => void;
  setModelSelectionsSerie: (v: ModelSelection[]) => void;
  setModelSelectionsExtra: (v: (ModelSelection | null)[]) => void;
  setMultiModelEnabled: (v: boolean) => void;
  setPoseIds: (v: string[]) => void;
  setMultiPoseEnabled: (v: boolean) => void;
  setFondoSelection: (v: FondoSelection | null) => void;
  setFormatoVal: (v: string | null) => void;
  setResolucionVal: (v: ResolucionImagen) => void;
  setOtraConfig: (v: string) => void;
  setAutoImageCount: (v: number) => void;
  setVistaIds: (v: string[]) => void;
  setCatalogoVistas: (v: VistaCatalogo[]) => void;
  setVistaProductosExcluidos: (v: Record<string, string[]>) => void;
  setMultiPresetIds: (v: string[]) => void;
  setAnuncioTitulo: (v: AnuncioFieldState) => void;
  setAnuncioDescripcion: (v: AnuncioFieldState) => void;
  setAnuncioCta: (v: AnuncioFieldState) => void;
  setAnuncioColors: (v: string[]) => void;
  setVideoDuration: (v: number) => void;
  setVideoMultiScene: (v: boolean) => void;
  setVideoGanchoId: (v: string | null) => void;
  setVideoVoiceGuion: (v: VoiceGuionValue) => void;
}

/** Lo que no se puede aplicar en el acto: hace falta que lleguen los catálogos. */
export interface PendienteDeRestoreV2 {
  presetId: string | null;
  selectedProductIds?: string[];
  multiSlotProductIds?: Record<string, string[]>;
}

/**
 * El id del preset del modo Modelo. En ese modo el restore vuelve con UNA sola pose y el
 * toggle de múltiples apagado: generar de nuevo no debe multiplicar imágenes sin un
 * opt-in explícito. En los otros kinds el array de poses ES el contrato del payload y se
 * restaura entero.
 */
const PRESET_MODELO = 'imagen-con-modelo';

/**
 * Vuelve a poner la configuración guardada y devuelve lo que queda pendiente de resolver
 * contra los catálogos (preset y productos, que llegan async).
 *
 * Tolerante con un blob viejo —de una pestaña abierta antes de un deploy— o incompleto:
 * cada campo se aplica sólo si vino con el tipo que corresponde, y lo que falte se queda
 * con el default del modo en vez de romper la pantalla.
 */
export function aplicarRestoreV2(
  blob: unknown,
  s: SettersDeRestoreV2,
): PendienteDeRestoreV2 {
  const b = (blob ?? {}) as Partial<RestoreV2>;

  if (typeof b.sinProducto === 'boolean') s.setSinProducto(b.sinProducto);
  if (b.imagenBase && typeof b.imagenBase === 'object') s.setImagenBase(b.imagenBase);
  if (b.modelSelection) s.setModelSelection(b.modelSelection);
  // La serie se restaura ENTERA, a diferencia de las poses: el "volver atrás" tiene que
  // devolver la misma tanda que se generó, y la grilla la muestra completa.
  if (Array.isArray(b.modelSelectionsSerie))
    s.setModelSelectionsSerie(b.modelSelectionsSerie);
  if (Array.isArray(b.modelSelectionsExtra))
    s.setModelSelectionsExtra(b.modelSelectionsExtra);
  if (typeof b.multiModelEnabled === 'boolean')
    s.setMultiModelEnabled(b.multiModelEnabled);

  if (Array.isArray(b.poseIds)) {
    const soloUna = b.presetId === PRESET_MODELO && b.poseIds.length > 1;
    const poses = soloUna ? [b.poseIds[0]] : b.poseIds;
    s.setPoseIds(poses);
    // La invariante de la UI: más de una pose ⇒ el toggle prendido. Se deriva y no se
    // toma del blob para que un blob recortado (el caso de arriba) no vuelva con el
    // toggle prendido y una sola pose.
    s.setMultiPoseEnabled(poses.length > 1);
  }

  if (b.fondoSelection) s.setFondoSelection(b.fondoSelection);
  if (b.formatoVal) s.setFormatoVal(b.formatoVal);
  if (b.resolucionVal) s.setResolucionVal(b.resolucionVal);
  if (typeof b.otraConfig === 'string') s.setOtraConfig(b.otraConfig);
  if (typeof b.autoImageCount === 'number') s.setAutoImageCount(b.autoImageCount);
  if (Array.isArray(b.vistaIds)) s.setVistaIds(b.vistaIds);
  if (Array.isArray(b.catalogoVistas)) s.setCatalogoVistas(b.catalogoVistas);
  if (b.vistaProductosExcluidos && typeof b.vistaProductosExcluidos === 'object')
    s.setVistaProductosExcluidos(b.vistaProductosExcluidos);
  if (Array.isArray(b.multiPresetIds)) s.setMultiPresetIds(b.multiPresetIds);

  if (b.anuncio) {
    if (b.anuncio.titulo) s.setAnuncioTitulo(b.anuncio.titulo);
    if (b.anuncio.descripcion) s.setAnuncioDescripcion(b.anuncio.descripcion);
    if (b.anuncio.cta) s.setAnuncioCta(b.anuncio.cta);
    if (Array.isArray(b.anuncio.colores)) s.setAnuncioColors(b.anuncio.colores);
  }

  if (b.video) {
    if (typeof b.video.duracion === 'number') s.setVideoDuration(b.video.duracion);
    if (typeof b.video.multiEscena === 'boolean')
      s.setVideoMultiScene(b.video.multiEscena);
    // `null` es un valor legítimo (sin gancho), así que no alcanza con la verdad.
    if (b.video.ganchoId !== undefined) s.setVideoGanchoId(b.video.ganchoId);
    if (b.video.voz) s.setVideoVoiceGuion(b.video.voz);
  }

  return {
    presetId: b.presetId || null,
    selectedProductIds: Array.isArray(b.selectedProductIds)
      ? b.selectedProductIds
      : undefined,
    multiSlotProductIds:
      b.multiSlotProductIds && typeof b.multiSlotProductIds === 'object'
        ? b.multiSlotProductIds
        : undefined,
  };
}

/**
 * Descarta la vuelta atrás pendiente.
 *
 * La usan los DOS CTA de la pantalla de resultados —"Listo" y "Seguir generando"—, que
 * llevan al inicio: sin esto, `/interfaz-v2` monta, lee el blob y reabre el modo con la
 * configuración de la tanda anterior, que no es a donde ninguno de los dos dice que va
 * (ADR-2026-09-10-seguir-generando-va-al-inicio).
 *
 * `sessionStorage` puede fallar (modo privado): nunca tira.
 */
export function olvidarRestoreV2(): void {
  try {
    sessionStorage.removeItem(CLAVE_RESTORE_V2);
  } catch {
    /* noop */
  }
}
